import { nextTick, ref, watch, type Ref } from 'vue'
import { localHLC, repairStoredHlcs } from '../../services/sync/hlc'
import { syncEngine } from '../../services/sync/syncEngine'
import { migrateBarLayout } from '../../utils/barLayout'
import { LEGACY_BAR_KEYS, legacyBarSettings, savePersisted, stableJson } from './load'
import { normalizeSettingValue } from './normalize'

// Saving the synced settings on this device, and keeping them in step with
// the cloud key by key: each key carries a hybrid logical clock (HLC) stamp,
// and the newer stamp wins.
//
// Only settings that differ from their defaults are saved, exported and
// synced, so a setting the writer never changed follows the app's default,
// even when that default changes in a later version. A setting set back to
// its default is sent as DEFAULT_MARKER, which tells other devices to use
// their own default.

const LOCAL_HLC_KEY = 'ink-settings-local-hlc'
const SYNCED_HLC_KEY = 'ink-settings-synced-hlc'
// Set once this device has cleared the cloud of values saved only because
// they were the defaults at the time (see syncSettingsWithCloud).
const DEFAULTS_CLEANED_KEY = 'ink-settings-defaults-cleaned'

/** A synced setting's value that means "the default". */
export const DEFAULT_MARKER = Object.freeze({ $default: true })

function isDefaultMarker(value: any): boolean {
  return !!value && typeof value === 'object' && value.$default === true && Object.keys(value).length === 1
}

type RemoteSettings = Record<string, { value: any; hlc: number }>

/** Repairs per-key timestamps from a broken clock in place (see repairStoredHlcs). */
function repairSettingHlcs(local: Record<string, number>, synced: Record<string, number>) {
  let repaired = false
  for (const key of new Set([...Object.keys(local), ...Object.keys(synced)])) {
    const fixed = repairStoredHlcs(local[key] ?? 0, synced[key] ?? 0)
    if (!fixed) continue
    local[key] = fixed.hlc
    synced[key] = fixed.syncedHlc
    repaired = true
  }
  if (!repaired) return
  try {
    localStorage.setItem(LOCAL_HLC_KEY, JSON.stringify(local))
    localStorage.setItem(SYNCED_HLC_KEY, JSON.stringify(synced))
  } catch { /* The repair runs again on the next launch. */ }
}

/**
 * Saves `refMap` whenever any of it changes, and pushes the changes when
 * settings sync is on. Returns the ways settings come in from elsewhere.
 */
export function useSettingsSync(
  refMap: Record<string, Ref<any>>,
  syncSettingsEnabled: Ref<boolean>,
  defaults: Record<string, any>,
) {
  const defaultJson = Object.fromEntries(Object.entries(defaults).map(([key, value]) => [key, stableJson(value)]))

  function isDefault(key: string, value: any): boolean {
    return stableJson(value) === defaultJson[key]
  }

  /** A fresh copy of a setting's default. */
  function defaultFor(key: string): any {
    return structuredClone(defaults[key])
  }

  /** The settings that differ from their defaults: what is saved and exported. */
  function changedSettings(): Record<string, any> {
    const values: Record<string, any> = {}
    for (const [key, r] of Object.entries(refMap)) {
      if (!isDefault(key, r.value)) values[key] = JSON.parse(JSON.stringify(r.value))
    }
    return values
  }

  /** A setting's value as sent to the cloud. */
  function toCloud(key: string, value: any): any {
    return isDefault(key, value) ? DEFAULT_MARKER : value
  }

  /** A value from the cloud or an import, made safe to use. */
  function fromCloud(key: string, value: any): any {
    return isDefaultMarker(value) ? defaultFor(key) : normalizeSettingValue(key, value)
  }

  const currentLocalHlcs = ref<Record<string, number>>(
    JSON.parse(localStorage.getItem(LOCAL_HLC_KEY) || '{}')
  )
  const lastSyncedHlcs = ref<Record<string, number>>(
    JSON.parse(localStorage.getItem(SYNCED_HLC_KEY) || '{}')
  )
  repairSettingHlcs(currentLocalHlcs.value, lastSyncedHlcs.value)

  function saveHlcs() {
    localStorage.setItem(LOCAL_HLC_KEY, JSON.stringify(currentLocalHlcs.value))
    localStorage.setItem(SYNCED_HLC_KEY, JSON.stringify(lastSyncedHlcs.value))
  }

  let syncDebounceTimer: any = null
  // Keys edited here since the last push. Only these are sent: each key is a
  // row of its own in the cloud, so sending every setting for one change
  // would write ~150 rows instead of one.
  const unsentKeys = new Set<string>()

  /** Sends the keys edited since the last push, then marks what went as synced. */
  async function pushUnsent() {
    if (syncDebounceTimer) clearTimeout(syncDebounceTimer)
    syncDebounceTimer = null
    if (unsentKeys.size === 0) return
    const fallbackHlc = localHLC.now()
    const changes = [...unsentKeys].map((key) => ({
      key,
      value: toCloud(key, refMap[key].value),
      hlc: currentLocalHlcs.value[key] || fallbackHlc,
    }))
    unsentKeys.clear()
    // pushSettings queues anything it can't send in the outbox, so once it
    // returns each change is on its way.
    await syncEngine.pushSettings(changes)
    for (const change of changes) {
      if (currentLocalHlcs.value[change.key] === change.hlc) lastSyncedHlcs.value[change.key] = change.hlc
    }
    saveHlcs()
  }

  // What each key held when the watcher last ran. A key that differs from
  // this is a local edit. The watcher runs on the next tick, after a remote
  // apply has finished, so remote values are recorded here as they are
  // applied (see markSeen) rather than skipped by a flag.
  let lastValues: Record<string, any> = {}
  for (const key of Object.keys(refMap)) {
    lastValues[key] = JSON.parse(JSON.stringify(refMap[key].value))
  }

  function markSeen(key: string) {
    lastValues[key] = JSON.parse(JSON.stringify(refMap[key].value))
  }

  // A save from an older version holds every setting; drop the defaults now.
  savePersisted(changedSettings())

  watch(
    Object.values(refMap),
    () => {
      const nowHlc = localHLC.now()
      let anyChanged = false

      const newSettings: Record<string, any> = {}
      for (const [key, r] of Object.entries(refMap)) {
        newSettings[key] = r.value
      }

      for (const key of Object.keys(refMap)) {
        if (JSON.stringify(newSettings[key]) !== JSON.stringify(lastValues[key])) {
          currentLocalHlcs.value[key] = nowHlc
          // With sync off, sign-in sends local edits (see syncSettingsWithCloud).
          if (syncSettingsEnabled.value) unsentKeys.add(key)
          anyChanged = true
        }
      }
      if (anyChanged) {
        localStorage.setItem(LOCAL_HLC_KEY, JSON.stringify(currentLocalHlcs.value))
      }

      lastValues = JSON.parse(JSON.stringify(newSettings))

      savePersisted(changedSettings())

      // Push after a pause, so a run of quick edits goes in one request.
      if (unsentKeys.size > 0) {
        if (syncDebounceTimer) clearTimeout(syncDebounceTimer)
        syncDebounceTimer = setTimeout(() => {
          pushUnsent().catch((err) => console.warn('[SettingsStore] Settings push failed:', err))
        }, 500)
      }
    },
    { deep: true }
  )

  /**
   * Builds the bar layouts from the old toolbar settings in a blob that has
   * them but no layouts. A device with no saved settings starts from the
   * default layouts, so synced settings may replace those (untouched ones
   * only); imported settings always do, as any import does.
   */
  function migrateBarLayoutsFrom(values: Record<string, any>, onlyIfUntouched: boolean) {
    if (!LEGACY_BAR_KEYS.some((key) => values[key] != null)) return
    for (const [device, key] of [['desktop', 'desktopBarLayout'], ['phone', 'phoneBarLayout']] as const) {
      if (values[key] != null || (onlyIfUntouched && currentLocalHlcs.value[key])) continue
      refMap[key].value = migrateBarLayout(device, legacyBarSettings(values))
      // Built from synced values, which every device can build alike.
      if (onlyIfUntouched) markSeen(key)
    }
  }

  function migrateBarLayoutsFromRemote(remote: RemoteSettings) {
    migrateBarLayoutsFrom(Object.fromEntries(Object.entries(remote).map(([key, item]) => [key, item?.value])), true)
  }

  /** Takes a remote value: the cloud's copy and this device's now agree on it. */
  function applyRemote(key: string, item: { value: any; hlc: number }) {
    refMap[key].value = fromCloud(key, item.value)
    markSeen(key)
    lastSyncedHlcs.value[key] = item.hlc
    currentLocalHlcs.value[key] = item.hlc
  }

  /**
   * Applies remote settings pulled from Cloudflare Worker, respecting HLC timestamps.
   */
  function applyRemoteSettings(remoteSettings: RemoteSettings) {
    if (!remoteSettings || typeof remoteSettings !== 'object') return
    for (const [key, item] of Object.entries(remoteSettings)) {
      if (!item || item.value === undefined || item.value === null) continue
      if (!refMap[key]) continue

      const localHlc = currentLocalHlcs.value[key] || 0
      const lastSyncedHlc = lastSyncedHlcs.value[key] || 0

      // If local has not changed since last sync, or remote is newer than local change
      if (localHlc <= lastSyncedHlc || item.hlc > localHlc) applyRemote(key, item)
    }
    migrateBarLayoutsFromRemote(remoteSettings)
    saveHlcs()
  }

  /**
   * Pulled remote settings from cloud, performs per-key LWW resolution,
   * overwriting safe local defaults while keeping and pushing back any pre-login changes.
   * Pass a pull that is already in hand to reuse it; otherwise this pulls on
   * its own. Returns whether the remote values were applied.
   */
  async function syncSettingsWithCloud(prePulled?: RemoteSettings): Promise<boolean> {
    if (!syncSettingsEnabled.value || !syncEngine.isAuthenticated()) return false

    try {
      // Older versions sent every setting, defaults included, so the cloud
      // may hold defaults as if the writer had chosen them. Once per device,
      // look at every row (not just the latest changes) to find them.
      const cleanUp = localStorage.getItem(DEFAULTS_CLEANED_KEY) !== '1'
      const remoteSettings = cleanUp
        ? await syncEngine.pullSettings({ full: true })
        : prePulled ?? await syncEngine.pullSettings()
      if (!remoteSettings) return false

      const localWins: { key: string; value: any; hlc: number }[] = []

      for (const key of Object.keys(refMap)) {
        const remoteItem = remoteSettings[key]
        const localHlc = currentLocalHlcs.value[key] || 0
        const lastSyncedHlc = lastSyncedHlcs.value[key] || 0

        if (localHlc > lastSyncedHlc) {
          // Local wins! Prepare to push it to the cloud.
          localWins.push({
            key,
            value: toCloud(key, refMap[key].value),
            hlc: localHlc,
          })
          lastSyncedHlcs.value[key] = localHlc // Mark local as synced
        } else if (remoteItem && remoteItem.value !== undefined && remoteItem.value !== null) {
          applyRemote(key, remoteItem)
        }
      }
      migrateBarLayoutsFromRemote(remoteSettings)

      // A row holding a setting's default as a plain value would keep it
      // there for good, even after the default changes; mark it as the
      // default instead.
      const winning = new Set(localWins.map((change) => change.key))
      for (const key of Object.keys(refMap)) {
        const remoteItem = remoteSettings[key]
        if (winning.has(key) || remoteItem?.value == null || isDefaultMarker(remoteItem.value)) continue
        if (!isDefault(key, refMap[key].value)) continue
        const hlc = localHLC.now()
        currentLocalHlcs.value[key] = hlc
        lastSyncedHlcs.value[key] = hlc
        localWins.push({ key, value: DEFAULT_MARKER, hlc })
      }
      saveHlcs()

      if (localWins.length > 0) {
        await syncEngine.pushSettings(localWins)
      }
      if (cleanUp) localStorage.setItem(DEFAULTS_CLEANED_KEY, '1')
      return true
    } catch (e) {
      console.warn('[SettingsStore] Settings sync with cloud failed:', e)
      return false
    }
  }

  /** The settings that differ from their defaults, keyed as in local storage. */
  function exportableSettings(): Record<string, any> {
    return changedSettings()
  }

  /**
   * Applies settings carried over by a corpus import, replacing them all:
   * a setting the import leaves out was at its default where it came from,
   * so it goes back to the default here. They are treated as local edits:
   * each changed key gets a fresh HLC, so the imported values win over the
   * cloud copy instead of being overwritten by it. Keys in `keep` are left
   * as they are: the file never carries them.
   */
  async function applyImportedSettings(values: Record<string, any>, keep: readonly string[] = []): Promise<void> {
    for (const [key, target] of Object.entries(refMap)) {
      if (keep.includes(key)) continue
      const value = values[key]
      target.value = value === undefined || value === null ? defaultFor(key) : fromCloud(key, value)
    }
    migrateBarLayoutsFrom(values, false)
    // Let the persistence watcher stamp HLCs and write local storage, then
    // push what changed immediately rather than waiting for its debounce.
    await nextTick()
    if (syncSettingsEnabled.value) await pushUnsent()
  }

  return { applyRemoteSettings, syncSettingsWithCloud, exportableSettings, applyImportedSettings }
}
